import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Trash2, Edit3, Eye, EyeOff, Save, X, Upload, Tag, ToggleLeft, ToggleRight, Tv, Copy, ExternalLink, AlertCircle, CheckCircle2, DollarSign, BarChart3, Gift, Radio, ShoppingCart } from 'lucide-react';
import {
  adminListShows, adminCreateShow, adminUpdateShow, adminDeleteShow, adminTogglePublish,
  uploadVideo,
  adminListPromos, adminCreatePromo, adminDeletePromo, adminTogglePromo,
  rokuStatus,
  adminGetAffiliates, adminSetAffiliates, adminAffiliateClicks,
  adminListVip, adminCreateVip, adminDeleteVip, adminToggleVip,
} from '../api/services';

const emptyShow = () => ({
  title: '', description: '', media_type: 'tv', poster_url: '', backdrop_url: '',
  trailer_youtube_id: '', year: '', genres: [], creator_name: '', seasons: [],
  pay_per_view: false, ppv_price_cents: 999, is_live: false,
  live_starts_at: null, live_ends_at: null,
});

const Studio = () => {
  const nav = useNavigate();
  const [tab, setTab] = useState('shows');
  const [shows, setShows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try { setShows(await adminListShows()); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editing.title || !editing.poster_url) { alert('Title and Poster URL required'); return; }
    const payload = { ...editing, genres: (editing.genres_text || '').split(',').map(s => s.trim()).filter(Boolean) };
    delete payload.genres_text;
    try {
      if (editing.show_id) await adminUpdateShow(editing.show_id, payload);
      else await adminCreateShow(payload);
      setEditing(null);
      await load();
    } catch (e) { alert(e?.response?.data?.detail || 'Save failed'); }
  };

  const onAddSeason = () => setEditing((e) => ({ ...e, seasons: [...(e.seasons || []), { season_number: (e.seasons?.length || 0) + 1, title: '', episodes: [] }] }));
  const onAddEpisode = (si) => setEditing((e) => {
    const s = [...(e.seasons || [])];
    s[si] = { ...s[si], episodes: [...(s[si].episodes || []), { _k: Math.random().toString(36).slice(2), title: '', youtube_id: '', video_url: '', duration_min: 0, description: '' }] };
    return { ...e, seasons: s };
  });

  return (
    <div className="min-h-screen px-8 py-8 max-w-7xl mx-auto">
      <div className="flex items-center justify-between">
        <div>
          <button onClick={() => nav(-1)} className="text-white/60 hover:text-white text-sm">← Back</button>
          <h1 className="font-display text-5xl text-white mt-2">BLUNT Studio</h1>
          <p className="text-white/60">Publish your shows and films to BLUNT TV.</p>
        </div>
        {tab === 'shows' && (
          <button
            onClick={() => setEditing({ ...emptyShow(), genres_text: '' })}
            className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-[#E50914] text-white font-semibold hover:bg-[#f6121d] tv-focusable"
          >
            <Plus size={18} /> New Show
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="mt-6 flex gap-2 border-b border-white/10 overflow-x-auto">
        {[
          { id: 'shows', label: 'Shows' },
          { id: 'promos', label: 'Promo Codes' },
          { id: 'vip', label: 'VIP Codes' },
          { id: 'roku', label: 'Roku Channel' },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-5 py-3 border-b-2 font-medium transition whitespace-nowrap ${tab === t.id ? 'border-[#E50914] text-white' : 'border-transparent text-white/60 hover:text-white'}`}
          >{t.label}</button>
        ))}
      </div>

      {tab === 'shows' ? (
        <div className="mt-8">
          {loading ? (
            <div className="text-white/50">Loading…</div>
          ) : shows.length === 0 ? (
            <div className="p-10 text-center rounded-2xl bg-white/5 border border-white/10">
              <div className="text-white/70">No shows yet. Click <span className="text-white">New Show</span> to publish your first one.</div>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {shows.map((s) => (
                <div key={s.show_id} className="rounded-2xl overflow-hidden bg-white/5 border border-white/10 group">
                  <div className="aspect-video relative bg-black">
                    <img src={s.backdrop_url || s.poster_url} alt={s.title} className="w-full h-full object-cover opacity-90" />
                    <div className="absolute top-2 right-2 flex gap-1">
                      <button onClick={async () => { await adminTogglePublish(s.show_id); load(); }} className={`px-2 py-1 rounded text-[11px] font-semibold ${s.published ? 'bg-emerald-500/90 text-white' : 'bg-white/15 text-white/80'}`}>
                        {s.published ? <Eye size={12} className="inline mr-1" /> : <EyeOff size={12} className="inline mr-1" />} {s.published ? 'Public' : 'Hidden'}
                      </button>
                    </div>
                  </div>
                  <div className="p-4">
                    <div className="font-semibold text-white">{s.title}</div>
                    <div className="text-white/50 text-xs mt-0.5">{s.media_type.toUpperCase()} • {s.year || '—'} • {s.seasons?.length || 0} season(s)</div>
                    <p className="text-white/60 text-sm mt-2 line-clamp-2">{s.description}</p>
                    <div className="flex gap-2 mt-4">
                      <button onClick={() => setEditing({ ...s, genres_text: (s.genres || []).join(', '), seasons: (s.seasons || []).map((se) => ({ ...se, episodes: (se.episodes || []).map((ep) => ({ ...ep, _k: Math.random().toString(36).slice(2) })) })) })} className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-white/10 hover:bg-white/15 text-white text-sm">
                        <Edit3 size={14} /> Edit
                      </button>
                      <button onClick={async () => { if (window.confirm('Delete this show?')) { await adminDeleteShow(s.show_id); load(); } }} className="px-3 py-2 rounded-lg bg-red-500/15 hover:bg-red-500/25 text-red-300 text-sm">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : tab === 'promos' ? (
        <PromosTab />
      ) : tab === 'vip' ? (
        <VipTab />
      ) : (
        <RokuTab />
      )}

      {editing && (
        <ShowEditor
          editing={editing}
          setEditing={setEditing}
          onClose={() => setEditing(null)}
          onSave={save}
          onAddSeason={onAddSeason}
          onAddEpisode={onAddEpisode}
        />
      )}
    </div>
  );
};

const Field = ({ label, value, onChange, placeholder }) => (
  <div>
    <label className="text-xs text-white/60">{label}</label>
    <input value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
      className="w-full mt-1 px-3 py-2 rounded-lg bg-white/5 border border-white/15 text-white placeholder:text-white/30" />
  </div>
);

const EpisodeRow = ({ season, si, ei, setEditing, editing }) => {
  const ep = season.episodes[ei];
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [loadedBytes, setLoadedBytes] = useState(0);
  const [fileSize, setFileSize] = useState(0);
  const abortRef = useRef(null);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const epKey = ep._k;
  const updateEp = (patch) => setEditing((prev) => {
    const list = prev?.seasons?.[si]?.episodes || [];
    const idx = epKey ? list.findIndex((x) => x._k === epKey) : ei;
    if (!list[idx]) return prev;
    const s = [...prev.seasons];
    const ep2 = [...s[si].episodes];
    ep2[idx] = { ...ep2[idx], ...patch };
    s[si] = { ...s[si], episodes: ep2 };
    return { ...prev, seasons: s };
  });

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024 * 1024) {
      alert('File too large (max 2 GB).');
      return;
    }
    e.target.value = '';
    setUploading(true);
    setProgress(0);
    setLoadedBytes(0);
    setFileSize(file.size);
    abortRef.current = new AbortController();
    try {
      const res = await uploadVideo(file, (pct, loaded) => { setProgress(pct); setLoadedBytes(loaded); }, abortRef.current.signal);
      // res.url is /api/uploads/file/... — prepend backend URL to make absolute
      const absolute = res.url.startsWith('http') ? res.url : `${backendUrl}${res.url}`;
      updateEp({ video_url: absolute });
    } catch (err) {
      if (!abortRef.current?.signal.aborted) alert(err?.response?.data?.detail || 'Upload failed — choose the same file again to resume.');
    } finally {
      setUploading(false);
    }
  };

  const removeEp = () => setEditing((prev) => {
    const s = [...prev.seasons];
    const ep2 = [...s[si].episodes];
    ep2.splice(ei, 1);
    s[si] = { ...s[si], episodes: ep2 };
    return { ...prev, seasons: s };
  });

  return (
    <div className="grid md:grid-cols-12 gap-2 items-center py-2">
      <input placeholder="Episode title" value={ep.title} onChange={(e) => updateEp({ title: e.target.value })}
        className="md:col-span-3 px-2 py-1.5 rounded bg-black/30 border border-white/15 text-white text-sm" />
      <input placeholder="YouTube video ID (or upload)" value={ep.youtube_id || ''} onChange={(e) => updateEp({ youtube_id: e.target.value })}
        className="md:col-span-3 px-2 py-1.5 rounded bg-black/30 border border-white/15 text-white text-sm" />
      <div className="md:col-span-4 flex items-center gap-2">
        {ep.video_url ? (
          <div className="flex items-center gap-2 w-full">
            <div className="flex-1 px-2 py-1.5 rounded bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-xs truncate" title={ep.video_url}>
              ✓ MP4 attached
            </div>
            <button onClick={() => updateEp({ video_url: '' })} className="text-red-300 text-xs px-2 py-1"><X size={12} /></button>
          </div>
        ) : uploading ? (
          <div className="flex items-center gap-2 w-full" data-testid="episode-upload-progress">
            <div className="flex-1 h-2 bg-white/10 rounded-full overflow-hidden">
              <div className="h-full bg-[#E50914] transition-[width] duration-300" style={{ width: `${progress}%` }} />
            </div>
            <span className="text-white/60 text-xs whitespace-nowrap" data-testid="episode-upload-progress-text">
              {progress}% · {(loadedBytes / 1048576).toFixed(0)}/{(fileSize / 1048576).toFixed(0)} MB
            </span>
            <button data-testid="episode-upload-cancel-btn" onClick={() => abortRef.current?.abort()} className="text-red-300 text-xs px-1"><X size={12} /></button>
          </div>
        ) : (
          <label data-testid="episode-upload-mp4-btn" className="cursor-pointer inline-flex items-center gap-2 px-2 py-1.5 rounded bg-white/10 hover:bg-white/15 text-white text-xs w-full justify-center">
            <Upload size={12} /> Upload MP4
            <input data-testid="episode-upload-mp4-input" type="file" accept="video/mp4,video/quicktime,video/webm" className="hidden" onChange={handleFile} />
          </label>
        )}
      </div>
      <input type="number" placeholder="min" value={ep.duration_min || ''} onChange={(e) => updateEp({ duration_min: Number(e.target.value) })}
        className="md:col-span-1 px-2 py-1.5 rounded bg-black/30 border border-white/15 text-white text-sm" />
      <button onClick={removeEp} className="md:col-span-1 px-2 py-1.5 text-red-300 text-sm"><Trash2 size={14} /></button>
    </div>
  );
};

const ShowEditor = ({ editing, setEditing, onClose, onSave, onAddSeason, onAddEpisode }) => (
  <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur p-4 md:p-10 overflow-y-auto">
    <div className="max-w-3xl mx-auto bg-[#0e0e14] border border-white/10 rounded-2xl p-6 md:p-8">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-semibold">{editing.show_id ? 'Edit show' : 'New show'}</h2>
        <button onClick={onClose} className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center"><X size={18} /></button>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Field label="Title*" value={editing.title} onChange={(v) => setEditing({ ...editing, title: v })} />
        <Field label="Year" value={editing.year} onChange={(v) => setEditing({ ...editing, year: v })} />
        <Field label="Poster Image URL*" value={editing.poster_url} onChange={(v) => setEditing({ ...editing, poster_url: v })} placeholder="https://..." />
        <Field label="Backdrop Image URL" value={editing.backdrop_url} onChange={(v) => setEditing({ ...editing, backdrop_url: v })} placeholder="https://..." />
        <Field label="Trailer YouTube ID" value={editing.trailer_youtube_id} onChange={(v) => setEditing({ ...editing, trailer_youtube_id: v })} placeholder="e.g. dQw4w9WgXcQ" />
        <div>
          <label className="text-xs text-white/60">Type</label>
          <select value={editing.media_type} onChange={(e) => setEditing({ ...editing, media_type: e.target.value })}
            className="w-full mt-1 px-3 py-2 rounded-lg bg-white/5 border border-white/15 text-white">
            <option value="tv">TV Series</option>
            <option value="movie">Movie</option>
            <option value="film">Short Film</option>
          </select>
        </div>
        <Field label="Creator name" value={editing.creator_name} onChange={(v) => setEditing({ ...editing, creator_name: v })} />
        <Field label="Genres (comma separated)" value={editing.genres_text || ''} onChange={(v) => setEditing({ ...editing, genres_text: v })} />
      </div>
      <div className="mt-4">
        <label className="text-xs text-white/60">Description</label>
        <textarea rows={3} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })}
          className="w-full mt-1 px-3 py-2 rounded-lg bg-white/5 border border-white/15 text-white" />
      </div>

      {/* PPV settings */}
      <div className="mt-6 p-5 rounded-xl bg-amber-500/5 border border-amber-500/20">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <ShoppingCart size={18} className="text-amber-400" />
            <h3 className="text-white font-semibold">Pay-Per-View (PPV)</h3>
          </div>
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <span className="text-sm text-white/70">Enable PPV</span>
            <input type="checkbox" checked={!!editing.pay_per_view}
              onChange={(e) => setEditing({ ...editing, pay_per_view: e.target.checked })}
              className="w-5 h-5 rounded accent-amber-500" />
          </label>
        </div>
        {editing.pay_per_view && (
          <div className="space-y-3">
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-white/60">Price (USD)</label>
                <div className="relative mt-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40">$</span>
                  <input
                    type="number" step="0.01" min="0.50"
                    value={((editing.ppv_price_cents || 999) / 100).toFixed(2)}
                    onChange={(e) => setEditing({ ...editing, ppv_price_cents: Math.max(50, Math.round(Number(e.target.value) * 100)) })}
                    className="w-full pl-7 pr-3 py-2 rounded-lg bg-white/5 border border-white/15 text-white"
                  />
                </div>
              </div>
              <div className="flex items-end">
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={!!editing.is_live}
                    onChange={(e) => setEditing({ ...editing, is_live: e.target.checked })}
                    className="w-5 h-5 rounded accent-amber-500" />
                  <span className="text-sm text-white/85 flex items-center gap-1"><Radio size={14} className="text-red-400" /> Live event</span>
                </label>
              </div>
            </div>
            {editing.is_live && (
              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-white/60">Live starts at</label>
                  <input
                    type="datetime-local"
                    value={editing.live_starts_at ? new Date(editing.live_starts_at).toISOString().slice(0, 16) : ''}
                    onChange={(e) => setEditing({ ...editing, live_starts_at: e.target.value ? new Date(e.target.value).toISOString() : null })}
                    className="w-full mt-1 px-3 py-2 rounded-lg bg-white/5 border border-white/15 text-white"
                  />
                </div>
                <div>
                  <label className="text-xs text-white/60">Live ends at (access expires)</label>
                  <input
                    type="datetime-local"
                    value={editing.live_ends_at ? new Date(editing.live_ends_at).toISOString().slice(0, 16) : ''}
                    onChange={(e) => setEditing({ ...editing, live_ends_at: e.target.value ? new Date(e.target.value).toISOString() : null })}
                    className="w-full mt-1 px-3 py-2 rounded-lg bg-white/5 border border-white/15 text-white"
                  />
                </div>
              </div>
            )}
            <div className="text-xs text-white/50">
              PPV titles are visible to everyone but playback requires either a subscription, a paid PPV purchase, or a VIP code.
              {editing.is_live ? ' Live events expire at the end time you set.' : ' Permanent purchases never expire.'}
            </div>
          </div>
        )}
      </div>

      <div className="mt-6">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-semibold">Seasons & episodes</h3>
          <button onClick={onAddSeason} className="text-sm px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/15"><Plus size={14} className="inline mr-1" /> Add season</button>
        </div>
        {(editing.seasons || []).map((season, si) => (
          <div key={si} className="mt-3 p-4 rounded-xl bg-white/5 border border-white/10">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-white/70 text-sm">Season</span>
                <input type="number" min="1" value={season.season_number} onChange={(e) => {
                  const s = [...editing.seasons]; s[si] = { ...s[si], season_number: Number(e.target.value) }; setEditing({ ...editing, seasons: s });
                }} className="w-16 px-2 py-1 rounded bg-black/30 border border-white/15 text-white" />
                <input placeholder="Season title (optional)" value={season.title || ''} onChange={(e) => {
                  const s = [...editing.seasons]; s[si] = { ...s[si], title: e.target.value }; setEditing({ ...editing, seasons: s });
                }} className="px-2 py-1 rounded bg-black/30 border border-white/15 text-white text-sm" />
              </div>
              <button onClick={() => { const s = [...editing.seasons]; s.splice(si, 1); setEditing({ ...editing, seasons: s }); }} className="text-red-300 text-sm"><Trash2 size={14} /></button>
            </div>
            <div className="mt-3 divide-y divide-white/5">
              {(season.episodes || []).map((ep, ei) => (
                <EpisodeRow key={season.episodes[ei]?._k || ei} season={season} si={si} ei={ei} setEditing={setEditing} editing={editing} />
              ))}
              <button onClick={() => onAddEpisode(si)} className="text-sm px-3 py-1.5 mt-2 rounded-lg bg-white/10 hover:bg-white/15"><Plus size={14} className="inline mr-1" /> Add episode</button>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-7 flex gap-3 justify-end">
        <button onClick={onClose} className="px-5 py-2.5 rounded-lg bg-white/10 hover:bg-white/15">Cancel</button>
        <button onClick={onSave} className="inline-flex items-center gap-2 px-6 py-2.5 rounded-lg bg-[#E50914] hover:bg-[#f6121d] text-white font-semibold"><Save size={16} /> Save</button>
      </div>
    </div>
  </div>
);

const PromosTab = () => {
  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ code: '', discount_type: 'percent', value: 10, applies_to: 'any', max_uses: '', enabled: true });

  const load = async () => {
    setLoading(true);
    try { setPromos(await adminListPromos()); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.code.trim()) { alert('Code required'); return; }
    try {
      await adminCreatePromo({
        code: form.code.trim().toUpperCase(),
        discount_type: form.discount_type,
        value: Number(form.value),
        applies_to: form.applies_to,
        max_uses: form.max_uses ? Number(form.max_uses) : null,
        enabled: form.enabled,
      });
      setForm({ code: '', discount_type: 'percent', value: 10, applies_to: 'any', max_uses: '', enabled: true });
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Create failed');
    }
  };

  return (
    <div className="mt-8 grid md:grid-cols-3 gap-6">
      <div className="md:col-span-1 p-6 rounded-2xl bg-white/5 border border-white/10">
        <div className="flex items-center gap-2 mb-4">
          <Tag size={18} className="text-[#E50914]" />
          <h3 className="font-semibold text-white">New promo code</h3>
        </div>
        <div className="space-y-3">
          <Field label="Code" value={form.code} onChange={(v) => setForm({ ...form, code: v.toUpperCase() })} placeholder="LAUNCH50" />
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs text-white/60">Type</label>
              <select value={form.discount_type} onChange={(e) => setForm({ ...form, discount_type: e.target.value })}
                className="w-full mt-1 px-3 py-2 rounded-lg bg-white/5 border border-white/15 text-white text-sm">
                <option value="percent">Percent (%)</option>
                <option value="flat">Flat ($ off, cents)</option>
              </select>
            </div>
            <Field label={form.discount_type === 'percent' ? 'Percent off' : 'Cents off'} value={form.value} onChange={(v) => setForm({ ...form, value: v })} />
          </div>
          <div>
            <label className="text-xs text-white/60">Applies to</label>
            <select value={form.applies_to} onChange={(e) => setForm({ ...form, applies_to: e.target.value })}
              className="w-full mt-1 px-3 py-2 rounded-lg bg-white/5 border border-white/15 text-white text-sm">
              <option value="any">Any plan</option>
              <option value="basic">Basic only</option>
              <option value="live">Live Events only</option>
            </select>
          </div>
          <Field label="Max uses (optional)" value={form.max_uses} onChange={(v) => setForm({ ...form, max_uses: v })} placeholder="unlimited" />
          <button onClick={create} className="w-full py-2.5 rounded-lg bg-[#E50914] hover:bg-[#f6121d] text-white font-semibold">Create code</button>
        </div>
      </div>

      <div className="md:col-span-2">
        {loading ? (
          <div className="text-white/50">Loading…</div>
        ) : promos.length === 0 ? (
          <div className="p-10 text-center rounded-2xl bg-white/5 border border-white/10 text-white/60">No promo codes yet.</div>
        ) : (
          <div className="rounded-2xl overflow-hidden border border-white/10 bg-white/5">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-white/5 text-white/60 text-left">
                  <th className="px-4 py-3">Code</th>
                  <th className="px-4 py-3">Discount</th>
                  <th className="px-4 py-3">Applies</th>
                  <th className="px-4 py-3">Uses</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {promos.map((p) => (
                  <tr key={p.code} className="border-t border-white/5">
                    <td className="px-4 py-3 font-mono text-white">{p.code}</td>
                    <td className="px-4 py-3 text-white/80">
                      {p.discount_type === 'percent' ? `${p.value}% off` : `$${(p.value/100).toFixed(2)} off`}
                    </td>
                    <td className="px-4 py-3 text-white/70 capitalize">{p.applies_to}</td>
                    <td className="px-4 py-3 text-white/70">{p.uses || 0}{p.max_uses ? ` / ${p.max_uses}` : ''}</td>
                    <td className="px-4 py-3">
                      <button onClick={async () => { await adminTogglePromo(p.code); load(); }} className={`inline-flex items-center gap-1 text-xs ${p.enabled ? 'text-emerald-400' : 'text-white/50'}`}>
                        {p.enabled ? <ToggleRight size={18} /> : <ToggleLeft size={18} />} {p.enabled ? 'Enabled' : 'Disabled'}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={async () => { if (window.confirm(`Delete ${p.code}?`)) { await adminDeletePromo(p.code); load(); } }} className="text-red-300 hover:text-red-200">
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Studio;

const VipTab = () => {
  const [codes, setCodes] = React.useState([]);
  const [shows, setShows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [form, setForm] = React.useState({
    code: '', code_type: 'subscription', days: 30, plan: 'live',
    show_id: '', max_uses: '', note: '', enabled: true,
  });

  const load = async () => {
    setLoading(true);
    try {
      const [c, s] = await Promise.all([adminListVip(), adminListShows()]);
      setCodes(c); setShows(s);
    } finally { setLoading(false); }
  };
  React.useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.code.trim()) { alert('Code required'); return; }
    if (form.code_type === 'show' && !form.show_id) { alert('Select a show'); return; }
    try {
      await adminCreateVip({
        code: form.code.trim().toUpperCase(),
        code_type: form.code_type,
        days: form.code_type === 'subscription' ? Number(form.days || 30) : null,
        plan: form.code_type !== 'show' ? form.plan : null,
        show_id: form.code_type === 'show' ? form.show_id : null,
        max_uses: form.max_uses ? Number(form.max_uses) : null,
        note: form.note || null,
        enabled: form.enabled,
      });
      setForm({ ...form, code: '', max_uses: '', note: '' });
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Create failed');
    }
  };

  return (
    <div className="mt-8 grid md:grid-cols-3 gap-6">
      <div className="md:col-span-1 p-6 rounded-2xl bg-white/5 border border-white/10">
        <div className="flex items-center gap-2 mb-4">
          <Gift size={18} className="text-amber-400" />
          <h3 className="font-semibold text-white">New VIP code</h3>
        </div>
        <div className="space-y-3">
          <Field label="Code" value={form.code} onChange={(v) => setForm({ ...form, code: v.toUpperCase() })} placeholder="PRESS2025" />

          <div>
            <label className="text-xs text-white/60">Code type</label>
            <select value={form.code_type} onChange={(e) => setForm({ ...form, code_type: e.target.value })}
              className="w-full mt-1 px-3 py-2 rounded-lg bg-white/5 border border-white/15 text-white text-sm">
              <option value="subscription">Time-limited subscription</option>
              <option value="lifetime">Lifetime subscription</option>
              <option value="show">Specific show (PPV comp)</option>
            </select>
          </div>

          {form.code_type === 'subscription' && (
            <div className="grid grid-cols-2 gap-2">
              <Field label="Days" value={form.days} onChange={(v) => setForm({ ...form, days: v })} placeholder="30" />
              <div>
                <label className="text-xs text-white/60">Plan</label>
                <select value={form.plan} onChange={(e) => setForm({ ...form, plan: e.target.value })}
                  className="w-full mt-1 px-3 py-2 rounded-lg bg-white/5 border border-white/15 text-white text-sm">
                  <option value="basic">Basic</option>
                  <option value="live">Live Events</option>
                </select>
              </div>
            </div>
          )}
          {form.code_type === 'lifetime' && (
            <div>
              <label className="text-xs text-white/60">Plan</label>
              <select value={form.plan} onChange={(e) => setForm({ ...form, plan: e.target.value })}
                className="w-full mt-1 px-3 py-2 rounded-lg bg-white/5 border border-white/15 text-white text-sm">
                <option value="basic">Basic</option>
                <option value="live">Live Events</option>
              </select>
            </div>
          )}
          {form.code_type === 'show' && (
            <div>
              <label className="text-xs text-white/60">Show</label>
              <select value={form.show_id} onChange={(e) => setForm({ ...form, show_id: e.target.value })}
                className="w-full mt-1 px-3 py-2 rounded-lg bg-white/5 border border-white/15 text-white text-sm">
                <option value="">— pick a show —</option>
                {shows.map((s) => <option key={s.show_id} value={s.show_id}>{s.title}</option>)}
              </select>
            </div>
          )}

          <Field label="Max uses (optional)" value={form.max_uses} onChange={(v) => setForm({ ...form, max_uses: v })} placeholder="unlimited" />
          <Field label="Note (internal)" value={form.note} onChange={(v) => setForm({ ...form, note: v })} placeholder="e.g. Press kit, contest" />

          <button onClick={create} className="w-full py-2.5 rounded-lg bg-amber-500 hover:bg-amber-400 text-black font-semibold">Create code</button>
        </div>
      </div>

      <div className="md:col-span-2">
        {loading ? (
          <div className="text-white/50">Loading…</div>
        ) : codes.length === 0 ? (
          <div className="p-10 text-center rounded-2xl bg-white/5 border border-white/10 text-white/60">No VIP codes yet.</div>
        ) : (
          <div className="rounded-2xl overflow-hidden border border-white/10 bg-white/5">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-white/5 text-white/60 text-left">
                  <th className="px-4 py-3">Code</th>
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3">Details</th>
                  <th className="px-4 py-3">Uses</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {codes.map((p) => {
                  const show = shows.find((s) => s.show_id === p.show_id);
                  return (
                    <tr key={p.code} className="border-t border-white/5">
                      <td className="px-4 py-3 font-mono text-white">{p.code}</td>
                      <td className="px-4 py-3 text-white/80 capitalize">{p.code_type}</td>
                      <td className="px-4 py-3 text-white/70 text-xs">
                        {p.code_type === 'subscription' && `${p.days} day${p.days === 1 ? '' : 's'} ${p.plan?.toUpperCase()}`}
                        {p.code_type === 'lifetime' && `Lifetime ${p.plan?.toUpperCase()}`}
                        {p.code_type === 'show' && (show ? show.title : p.show_id)}
                        {p.note && <div className="text-white/40 mt-0.5">{p.note}</div>}
                      </td>
                      <td className="px-4 py-3 text-white/70">{p.uses || 0}{p.max_uses ? ` / ${p.max_uses}` : ''}</td>
                      <td className="px-4 py-3">
                        <button onClick={async () => { await adminToggleVip(p.code); load(); }}
                          className={`inline-flex items-center gap-1 text-xs ${p.enabled ? 'text-emerald-400' : 'text-white/50'}`}>
                          {p.enabled ? <ToggleRight size={18} /> : <ToggleLeft size={18} />} {p.enabled ? 'Enabled' : 'Disabled'}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button onClick={async () => { if (window.confirm(`Delete ${p.code}?`)) { await adminDeleteVip(p.code); load(); } }}
                          className="text-red-300 hover:text-red-200"><Trash2 size={14} /></button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const RokuTab = () => {
  const [status, setStatus] = React.useState(null);
  const [copied, setCopied] = React.useState(false);

  React.useEffect(() => { rokuStatus().then(setStatus).catch(() => {}); }, []);

  const feedUrl = status?.feed_url || '';
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(feedUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  };

  return (
    <div className="mt-8 max-w-4xl">
      <div className="p-8 rounded-2xl bg-gradient-to-br from-[#662D91]/20 via-white/5 to-transparent border border-[#662D91]/30">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-[#662D91] flex items-center justify-center text-white">
            <Tv size={22} />
          </div>
          <div>
            <h3 className="text-xl font-semibold text-white">Roku Direct Publisher Feed</h3>
            <p className="text-white/60 text-sm">Publish a Roku channel from your BLUNT Originals catalog.</p>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-3 gap-3">
          <div className="p-4 rounded-xl bg-white/5 border border-white/10">
            <div className="text-[11px] uppercase tracking-wider text-white/50">Roku-ready episodes</div>
            <div className="text-3xl font-bold text-white mt-1">{status?.roku_ready_episodes ?? '…'}</div>
            <div className="text-xs text-white/50 mt-1">mp4 uploaded</div>
          </div>
          <div className="p-4 rounded-xl bg-white/5 border border-white/10">
            <div className="text-[11px] uppercase tracking-wider text-white/50">YouTube-only</div>
            <div className="text-3xl font-bold text-amber-400 mt-1">{status?.youtube_only_episodes ?? '…'}</div>
            <div className="text-xs text-white/50 mt-1">won't appear on Roku</div>
          </div>
          <div className="p-4 rounded-xl bg-white/5 border border-white/10">
            <div className="text-[11px] uppercase tracking-wider text-white/50">Published shows</div>
            <div className="text-3xl font-bold text-white mt-1">{status?.published_shows ?? '…'}</div>
          </div>
        </div>

        <div className="mt-6">
          <div className="text-[11px] uppercase tracking-wider text-white/50 mb-2">Your Roku JSON feed URL</div>
          <div className="flex gap-2">
            <input
              readOnly
              value={feedUrl}
              className="flex-1 px-4 py-3 rounded-lg bg-black/40 border border-white/15 text-white font-mono text-sm"
              onClick={(e) => e.target.select()}
            />
            <button onClick={copy} className="px-4 py-3 rounded-lg bg-white text-black font-semibold hover:bg-white/90 inline-flex items-center gap-2">
              <Copy size={16} /> {copied ? 'Copied!' : 'Copy'}
            </button>
            <a href={feedUrl} target="_blank" rel="noreferrer" className="px-4 py-3 rounded-lg bg-white/10 text-white hover:bg-white/15 inline-flex items-center gap-2">
              <ExternalLink size={16} /> Preview
            </a>
          </div>
        </div>

        {status?.youtube_only_episodes > 0 && (
          <div className="mt-5 p-4 rounded-xl bg-amber-500/10 border border-amber-500/30 flex gap-3">
            <AlertCircle size={18} className="text-amber-400 shrink-0 mt-0.5" />
            <div className="text-sm text-amber-200">
              {status.youtube_only_episodes} episode(s) use YouTube links — Roku Direct Publisher does NOT support YouTube.
              Edit each episode and click <span className="text-white font-semibold">Upload MP4</span> to make it Roku-ready.
            </div>
          </div>
        )}

        {status?.roku_ready_episodes > 0 && (
          <div className="mt-3 p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/30 flex gap-3">
            <CheckCircle2 size={18} className="text-emerald-400 shrink-0 mt-0.5" />
            <div className="text-sm text-emerald-200">
              Your feed is live with {status.roku_ready_episodes} episode(s). Submit the URL above at developer.roku.com → Direct Publisher.
            </div>
          </div>
        )}
      </div>

      <div className="mt-6 p-6 rounded-2xl bg-white/5 border border-white/10">
        <h4 className="text-white font-semibold mb-4">How to publish your Roku channel</h4>
        <ol className="space-y-3 text-white/80 text-sm list-decimal list-inside">
          <li>Upload at least 1 mp4 to any episode via the <span className="text-white font-semibold">Shows</span> tab (YouTube-only shows won't appear on Roku).</li>
          <li>Sign up for a free Roku developer account at <a className="text-[#E50914] hover:underline" href="https://developer.roku.com" target="_blank" rel="noreferrer">developer.roku.com</a>.</li>
          <li>Go to <span className="text-white font-semibold">Direct Publisher</span> → <span className="text-white font-semibold">Create New Channel</span>.</li>
          <li>Paste the JSON feed URL above. Roku will validate and preview your content.</li>
          <li>Fill in channel branding (icon, screenshots, category) and submit for review. Approval takes 5-10 business days.</li>
          <li>After approval, anyone can install "BLUNT TV" from the Roku Channel Store — no side-loading needed.</li>
        </ol>
        <div className="mt-4 text-xs text-white/50">
          Tip: The feed updates automatically. Every time you publish or upload a new episode, it's immediately available to your Roku channel.
        </div>
      </div>
    </div>
  );
};